import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import {
  SingleSelectFilterBase,
  SingleSelectFilterBaseProps,
} from '../SingleSelectFilterBase';
import {
  ListItem,
  useBuildGroupedOptions,
  useBuildOption,
  useDropdownSelectFilterState,
} from '../../hooks';

export interface SingleSelectFilterProps<V>
  extends Omit<
    SingleSelectFilterBaseProps<V>,
    'keyedItems' | 'options' | 'dropdownSelect'
  > {
  /**
   * List of items to select from
   * @external
   */
  data: V[];
  /**
   * A function that returns a [ListItemSelectProps](https://www.docs.wixdesignsystem.com/?path=/story/components-form-dropdownlistitems--listitemselect) (Optional)
   * @external
   */
  renderItem?: (item: V, index: number) => ListItem;
}

function _SingleSelectFilter<V>(props: SingleSelectFilterProps<V>) {
  const { renderItem, renderSection, filter, data, ...rest } = props;

  const { dropdownSelect, init } = useDropdownSelectFilterState<V>({
    filter,
  });

  useEffect(() => {
    return init();
  }, []);

  const keyedItems = useMemo(
    () =>
      data.map((item, index) => {
        const key = filter.itemKey(item);
        return {
          id: key,
          key,
          item,
          index,
          indexWithinPage: index,
          pageIndex: 0,
        };
      }),
    [data],
  );

  const options = useBuildGroupedOptions({
    items: keyedItems.map(
      useBuildOption({
        filter,
        renderItem,
      }),
    ),
    renderSection,
  });

  return (
    <SingleSelectFilterBase
      {...rest}
      options={options}
      disabled={!options.length}
      renderItem={renderItem}
      filter={filter}
      renderSection={renderSection}
      dropdownSelect={dropdownSelect}
    />
  );
}

export const SingleSelectFilter = observer(_SingleSelectFilter);
